import React, { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'clsx';
import { ChevronDownIcon } from '@fronton/icons-react';
import { Typography, TypographyHeaderProps, TypographyTextProps } from 'common/components';
import {
  servicePanelGroupName,
  PanelHeader,
  openServicePanelHeader,
  servicePanelIcon,
  panelArrowIcon,
  openPanelArrowIcon,
  ServicePanelBody,
} from './service-panel-styles';

export type ServicePanelProps = {
  children: React.ReactNode;
  isOpen?: boolean;
  name: string;
  onClick?: (panelName: string, isOpen: boolean) => void;
  renderIcon: (visible: boolean) => React.ReactNode;
  title: string;
} & Partial<TypographyHeaderProps | TypographyTextProps> &
  Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'onClick'>;

export const ServicePanel: React.FC<ServicePanelProps> = ({
  children,
  isOpen = false,
  name,
  onClick,
  renderIcon,
  title,
}) => {
  const servicePanelBodyRef = useRef<HTMLDivElement | null>(null);
  const [servicePanelBodyHeight, setServicePanelBodyHeight] = useState(isOpen ? '100%' : '0px');

  useEffect(() => {
    if (isOpen && !servicePanelBodyRef.current) {
      setServicePanelBodyHeight('100%');
    }

    if (servicePanelBodyRef.current) {
      setServicePanelBodyHeight(isOpen ? `${servicePanelBodyRef.current.scrollHeight}px` : '0px');
    }
  }, [isOpen]);

  const handleServicePanelClick = useCallback(() => {
    if (onClick) onClick(name, isOpen);
  }, [onClick, name, isOpen]);

  return (
    <div>
      <PanelHeader className={cn({ [openServicePanelHeader]: isOpen })} onClick={handleServicePanelClick}>
        {renderIcon && <div className={servicePanelIcon}>{renderIcon(isOpen)}</div>}
        {title && (
          <Typography className={servicePanelGroupName} variant="h3" as="h3">
            {title}
          </Typography>
        )}
        <ChevronDownIcon
          className={cn(panelArrowIcon, { [openPanelArrowIcon]: isOpen })}
          color="text-minor"
          size="l"
          type="outline"
        />
      </PanelHeader>
      <ServicePanelBody ref={servicePanelBodyRef} height={servicePanelBodyHeight} isOpen={isOpen}>
        {children}
      </ServicePanelBody>
    </div>
  );
};
